import { v4 as uuidv4 } from "uuid";

export const getCurrentPathname = () => {
   const pathnameArray = window.location.pathname.split("/");
   return pathnameArray[pathnameArray.length - 1];
};

export const debounce = (func, delay) => {
   let timeoutId;

   return function(...args) {
      if (timeoutId) {
         clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
         func.apply(this, args);
      }, delay);
   };
};

// Reverse of answerPayload below: takes one answer from the
// Surveys/{id}/{languageId}/with-answers response (lastSubmission.answers[])
// and normalizes its answerDetail into the exact shape the question-preview
// components read from useSurveyInputStore's `inputs` (see setDraftSurvey).
// Field names mostly match already - the one exception is the Ranking type
// (278), where the backend calls the field "choices" (with a `rank` per
// item) while the renderer reads `answerRankings`.
export const mapSubmissionAnswerToAnswerDetail = (questionType, answerDetail) => {
   if (questionType === 278) {
      const { choices, ...rest } = answerDetail;
      return { ...rest, answerRankings: choices || [] };
   }

   if (questionType === 106) {
      const file = answerDetail.answerFiles?.[0];
      if (!file) return { ...answerDetail, answerFiles: null };

      const isAbsoluteUrl = /^https?:\/\//i.test(file.filePath || "");
      const resolvedUrl = isAbsoluteUrl
         ? file.filePath
         : `${process.env.REACT_APP_FILE_MANAGEMENT}FileData/getFileByFileKey?platformId=${process.env.REACT_APP_PLATFORM_KEY}&fileKey=${file.filePath}`;

      return {
         ...answerDetail,
         answerFiles: {
            base64: resolvedUrl,
            name: file.filePath?.split("/").pop() || file.id,
            type: undefined,
            size: undefined
         }
      };
   }

   return answerDetail;
};

export const mapSubmissionAnswersToInputs = (answers = []) =>
   answers.reduce((acc, answer) => {
      // No answerDetail means this question wasn't answered - skip it entirely
      // so it's treated exactly like a question that's simply missing from
      // `answers`, rather than an empty object that could still trip up a
      // type-specific renderer expecting populated fields (e.g. SelectOne
      // reading `.choices[0]` with no guard on `choices` itself).
      if (!answer.answerDetail) return acc;

      acc[answer.questionId] = {
         id: answer.answerId,
         questionId: answer.questionId,
         sectionId: answer.sectionId,
         maxScore: answer.maxScore,
         questionType: answer.questionType,
         score: answer.score,
         answerDetail: mapSubmissionAnswerToAnswerDetail(answer.questionType, answer.answerDetail)
      };
      return acc;
   }, {});

export const collectModuleQuestionIds = (surveyItems = [], ids = new Set()) => {
   (surveyItems || []).forEach((item) => {
      if (item?.itemLabel === "Section") {
         collectModuleQuestionIds(item.innerItems, ids);
      } else if (item?.surveyItem?.id) {
         ids.add(item.surveyItem.id);
      }
   });
   return ids;
};

// useSurveyInputStore is shared by every module and keyed only by question, so anything sent
// to the backend has to be narrowed to the module being submitted or drafted.
export const pickModuleInputs = (inputs, moduleQuestionIds) => {
   const moduleInputs = {};
   const foreignQuestionIds = [];
   Object.entries(inputs).forEach(([key, input]) => {
      const questionId = input?.questionId ?? key;
      if (moduleQuestionIds.has(questionId)) {
         moduleInputs[key] = input;
      } else {
         foreignQuestionIds.push(questionId);
      }
   });
   return { moduleInputs, foreignQuestionIds };
};

export const answerPayload = (questionId, formData, index, inputs, input) => {
   const types = {
      34: "text",
      32: "choices",
      33: "choices",
      35: "number",
      36: "number",
      37: "text",
      38: "text",
      100: "number",
      104: "text",
      106: "answerFiles",
      112: "questionChoice",
      275: "number",
      277: "Matrix",
      278: "Ranking",
      276: "Rating"
   };

   function base64ToBlob(base64, mimeType) {
      const byteCharacters = atob(base64);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
         const slice = byteCharacters.slice(offset, offset + 512);

         const byteNumbers = new Array(slice.length);
         for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
         }

         const byteArray = new Uint8Array(byteNumbers);
         byteArrays.push(byteArray);
      }

      return new Blob(byteArrays, { type: mimeType });
   }
   switch (questionId) {
      case 32:
      case 33:
         if (input?.answerDetail?.text) {
            for (let i = 0; i < Object.values(inputs)[index].answerDetail.text.length; i++) {
               formData.append(
                  `answers[${index}].answerDetail.text`,
                  Object.values(inputs)[index].answerDetail.text
               );
            }
         } else {
            for (let i = 0; i < Object.values(inputs)[index].answerDetail.choices.length; i++) {
               formData.append(
                  `answers[${index}].answerDetail.choices[${i}].choiceId`,
                  Object.values(inputs)[index].answerDetail.choices[i]?.choiceId
               );
               if (Object.values(inputs)[index].answerDetail.choices[i]?.score != null) {
                  formData.append(
                     `answers[${index}].answerDetail.choices[${i}].score`,
                     Object.values(inputs)[index].answerDetail.choices[i]?.score
                  );
               }
            }
         }
         if (Object.values(inputs)[index]?.maxScore) {
            formData.append(`answers[${index}].maxScore`, Object.values(inputs)[index].maxScore);
         }
         break;
      case 278:
         for (let i = 0; i < Object.values(inputs)[index].answerDetail.answerRankings.length; i++) {
            formData.append(
               `answers[${index}].answerDetail.answerRankings[${i}].choiceId`,
               Object.values(inputs)[index].answerDetail.answerRankings[i].choiceId
            );
            formData.append(
               `answers[${index}].answerDetail.answerRankings[${i}].rank`,
               Object.values(inputs)[index].answerDetail.answerRankings[i].rank
            );
         }
         break;
      case 277:
         for (let i = 0; i < Object.values(inputs)[index].answerDetail.answerMatrix.length; i++) {
            formData.append(
               `answers[${index}].answerDetail.answerMatrix[${i}].choiceId`,
               Object.values(inputs)[index].answerDetail.answerMatrix[i].choiceId
            );
            formData.append(
               `answers[${index}].answerDetail.answerMatrix[${i}].secondChoiceId`,
               Object.values(inputs)[index].answerDetail.answerMatrix[i].secondChoiceId
            );
            formData.append(
               `answers[${index}].answerDetail.answerMatrix[${i}].text`,
               Object.values(inputs)[index].answerDetail.answerMatrix[i].text
            );
         }
         break;
      case 276:
         for (let i = 0; i < Object.values(inputs)[index].answerDetail.answerRatings.length; i++) {
            formData.append(
               `answers[${index}].answerDetail.answerRatings[${i}].choiceId`,
               Object.values(inputs)[index].answerDetail.answerRatings[i].choiceId
            );
            formData.append(
               `answers[${index}].answerDetail.answerRatings[${i}].secondChoiceId`,
               Object.values(inputs)[index].answerDetail.answerRatings[i].secondChoiceId
            );
         }
         break;
      case 106:
         const base64String = Object.values(inputs)[index].answerDetail.answerFiles?.base64.split(
            ","
         )[1];
         const mimeType = Object.values(inputs)
            [index].answerDetail.answerFiles?.base64.split(",")[0]
            .match(/:(.*?);/)[1];
         const blob = base64ToBlob(base64String, mimeType);

         formData.append(
            `answers[${index}].answerDetail.answerFiles`,
            blob,
            `${Object.values(inputs)[index].answerDetail.answerFiles.name}`
         );
         break;
      default:
         formData.append(
            `answers[${index}].answerDetail.${types[questionId]}`,
            Object.values(inputs)[index].answerDetail[types[questionId]]
         );
         if (questionId === 34) {
            formData.append(
               `answers[${index}].answerDetail.expectedAnswer`,
               Object.values(inputs)[index].expectedAnswer
            );
            if (Object.values(inputs)[index]?.maxScore) {
               formData.append(`answers[${index}].maxScore`, Object.values(inputs)[index].maxScore);
            }
         }
         break;
   }
};

export function getGeolocation() {
   return new Promise((resolve, reject) => {
      if ("geolocation" in navigator) {
         navigator.geolocation.getCurrentPosition(
            (position) => {
               const geolocation = {
                  lat: position.coords.latitude,
                  long: position.coords.longitude
               };
               resolve(geolocation);
            },
            (error) => {
               reject(error);
            }
         );
      } else {
         reject("Geolocation is not available in this browser.");
      }
   });
}

const createQuestion = (question) => {
   const id = uuidv4();
   return {
      itemLabel: "Question",
      id: id,
      surveyItem: {
         title: "",
         name: "",
         description: null,
         questionType: question.questionType,
         order: question.order,
         sectionId: question.sectionId,
         created: true,
         isRequired: false,
         questionLabels: [],
         surveyQuestionHints: [],
         selectQuestionChoices: [],
         range: null,
         secondChoices: [],
         id: id
      }
   };
};

const createInnerSection = (order) => {
   const id = uuidv4();
   return {
      itemLabel: "Section",
      id: id,
      sectionId: id,
      surveyItem: {
         description: "",
         order: order
      },
      innerItems: [],
      created: true
   };
};

// export const addQuestion = (survey, payload) => {
//    const { order, sectionId } = payload;
//    survey.forEach((item) => {
//       if (item.itemLabel === "Section") {
//          if (item.id !== sectionId) {
//             addQuestion(item.innerItems, payload);
//          } else {
//             console.log("dwdw ", item);
//             for (let i = order - 1; i < item.innerItems.length; i++) {
//                item.innerItems[i].surveyItem.order += 1;
//             }
//             item.innerItems.splice(order, 0, createQuestion(payload));
//          }
//       }
//    });
// };
export const addQuestion = (survey, payload) => {
   const { order, sectionId } = payload;

   survey.forEach((item) => {
      if (item.itemLabel === "Section" && item.id === sectionId) {

         item.innerItems.splice(order - 1, 0, createQuestion(payload));

         item.innerItems.forEach((innerItem, index) => {
            innerItem.surveyItem.order = index + 1;
         });
      } else if (item.innerItems) {
         addQuestion(item.innerItems, payload);
      }
   });
};

export const addInnerSection = (survey, payload, depth = 0) => {
   const { order, sectionId } = payload;
   survey.forEach((item) => {
      if (item.itemLabel === "Section") {
         if (item.id !== sectionId) {
            addInnerSection(item.innerItems, payload, depth + 1);
         } else if (depth === 0) {
            for (let i = order - 1; i < item.innerItems.length; i++) {
               item.innerItems[i].surveyItem.order += 1;
            }
            item.innerItems.splice(order, 0, createInnerSection(order));
         }
         // depth > 0 && item.id === sectionId: target is already an inner section — a
         // section can only have one level of inner sections, so no-op here.
      }
   });
};

const getParentSurveyOrder = (data, targetId) => {
   if (Array.isArray(data)) {
      for (const item of data) {
         const parentSurveyOrder = getParentSurveyOrder(item, targetId);
         if (parentSurveyOrder !== null) {
            return parentSurveyOrder;
         }
      }
   } else {
      if (data.id === targetId) {
         return data.surveyItem.order;
      }

      if (data?.innerItems && data?.innerItems?.length > 0) {
         for (const item of data.innerItems) {
            if (item.id === targetId) {
               return data.surveyItem.order;
            }

            const parentSurveyOrder = getParentSurveyOrder(item, targetId);
            if (parentSurveyOrder !== null) {
               return parentSurveyOrder;
            }
         }
      }
   }

   return null;
};

export const addSection = (survey, targetId) => {
   const parentOrder = getParentSurveyOrder(survey, targetId);

   for (let i = parentOrder; i < survey.length; i++) {
      survey[i].surveyItem.order += 1;
   }
   survey.splice(parentOrder + 1, 0, createInnerSection(parentOrder + 1));
};
export const addFirstSection = (survey) => {
   for (let i = 0; i < survey.length; i++) {
      survey[i].surveyItem.order += 1;
   }
   survey.splice(0, 0, createInnerSection(1));
};

export const deleteQuestion = (survey, sectionId, questionId) => {
   for (const item of survey) {
      if (item.itemLabel === "Section") {
         if (item.id !== sectionId) {
            deleteQuestion(item.innerItems, sectionId, questionId);
         } else {
            const index = item.innerItems.findIndex((item) => item.id === questionId);
            if (index > -1) {
               for (let i = index + 1; i < item.innerItems.length; i++) {
                  item.innerItems[i].surveyItem.order -= 1;
               }
               item.innerItems.splice(index, 1);
               break;
            }
         }
      }
   }
};

export const deleteSection = (survey, sectionId) => {
   for (const item of survey) {
      if (item.itemLabel === "Section") {
         if (item.id !== sectionId) {
            deleteSection(item.innerItems, sectionId);
         } else {
            const index = survey.findIndex((item) => item.id === sectionId);
            if (index > -1) {
               for (let i = index + 1; i < survey.length; i++) {
                  survey[i].surveyItem.order -= 1;
               }
               survey.splice(index, 1);
               break;
            }
         }
      }
   }
};

export const duplicateQuestion = (survey, sectionId, questionId) => {
   survey.forEach((item) => {
      if (item.itemLabel === "Section") {
         if (item.id !== sectionId) {
            duplicateQuestion(item.innerItems, sectionId, questionId);
         } else {
            const index = item.innerItems.findIndex((item) => item.id === questionId);
            if (index > -1) {
               for (let i = index + 1; i < item.innerItems.length; i++) {
                  item.innerItems[i].surveyItem.order += 1;
               }

               // const id = uuidv4();
               const questionItems = {
                  ...item.innerItems[index].surveyItem,
                  order: item.innerItems[index].surveyItem.order + 1,
                  // id: id
                  id: ""
               };

               item.innerItems.splice(index, 0, {
                  ...item.innerItems[index],
                  // id: id,
                  id: "",
                  surveyItem: questionItems
               });
            }
         }
      }
   });
   console.log({ survey });
};

export const updateQuestionHint = (survey, sectionId, questionId, text) => {
   survey.forEach((item) => {
      if (item.itemLabel === "Section") {
         if (item.id !== sectionId) {
            updateQuestionHint(item.innerItems, sectionId, questionId, text);
         } else {
            const index = item.innerItems.findIndex((item) => item.id === questionId);
            if (index > -1) {
               const hint = [{ text: text }];
               item.innerItems[index].surveyItem.surveyQuestionHints = hint;
            }
         }
      }
   });

   return survey;
};

export const updateIsRequiredStatus = (survey, questionId, status) => {
   const findAndUpdateQuestion = (items) => {
      items.forEach((item) => {
         if (item.itemLabel === "Section") {
            const questionIndex = item.innerItems.findIndex((q) => q.id === questionId);
            if (questionIndex > -1) {
               // Update the isRequired status
               item.innerItems[questionIndex].surveyItem.isRequired = status;
            } else {
               findAndUpdateQuestion(item.innerItems);
            }
         }
      });
   };

   findAndUpdateQuestion(survey);
   return survey;
};

export const updateQuestionLabel = (survey, sectionId, questionId, text) => {
   survey.forEach((item) => {
      if (item.itemLabel === "Section") {
         if (item.id !== sectionId) {
            if (item.id === questionId) {
               item.surveyItem.description = text;
            }
            updateQuestionLabel(item.innerItems, sectionId, questionId, text);
         } else {
            const index = item.innerItems.findIndex((item) => item.id === questionId);
            if (index > -1) {
               const questionLabels = [{ text: text }];
               item.innerItems[index].surveyItem.questionLabels = questionLabels;
            }
         }
      }
   });

   return survey;
};

export const updateQuestionType = (survey, sectionId, questionId, type) => {
   survey.forEach((item) => {
      if (item.itemLabel === "Section") {
         if (item.id !== sectionId) {
            updateQuestionType(item.innerItems, sectionId, questionId, type);
         } else {
            const index = item.innerItems.findIndex((item) => item.id === questionId);
            if (index > -1) {
               item.innerItems[index].surveyItem.questionType = type;
            }
         }
      }
   });

   return survey;
};

export const updateQuestionChoices = (survey, sectionId, questionId, choices) => {
   for (const item of survey) {
      if (item.itemLabel === "Section") {
         if (item.id !== sectionId) {
            updateQuestionChoices(item.innerItems, sectionId, questionId, choices);
         } else {
            const index = item.innerItems.findIndex((item) => item.id === questionId);
            if (index > -1) {
               item.innerItems[index].surveyItem.selectQuestionChoices = choices;
               break;
            }
         }
      }
   }
   return survey;
};

export const updateMaxChoices = (survey, sectionId, questionId, maxChoices) => {
   for (const item of survey) {
      if (item.itemLabel === "Section") {
         if (item.id !== sectionId) {
            updateMaxChoices(item.innerItems, sectionId, questionId, maxChoices);
         } else {
            const index = item.innerItems.findIndex((item) => item.id === questionId);
            if (index > -1) {
               item.innerItems[index].surveyItem.maxChoices = maxChoices; // Update maxChoices property
               break;
            }
         }
      }
   }
   return survey;
};

export const updateSecondQuestionChoices = (survey, sectionId, questionId, choices) => {
   for (const item of survey) {
      if (item.itemLabel === "Section") {
         if (item.id !== sectionId) {
            updateSecondQuestionChoices(item.innerItems, sectionId, questionId, choices);
         } else {
            const index = item.innerItems.findIndex((item) => item.id === questionId);
            if (index > -1) {
               // const mappedChoices = choices.map((choice) => {
               //    return {
               //       // id: uuidv4(),
               //       choiceLabels: [choice]
               //    };
               // });
               item.innerItems[index].surveyItem.secondChoices = choices;
               break;
            }
         }
      }
   }
   return survey;
};

export const updateRangeValues = (survey, sectionId, questionId, rangeOptions) => {
   for (const item of survey) {
      if (item.itemLabel === "Section") {
         if (item.id !== sectionId) {
            updateRangeValues(item.innerItems, sectionId, questionId, rangeOptions);
         } else {
            const index = item.innerItems.findIndex((item) => item.id === questionId);
            if (index > -1) {
               item.innerItems[index].surveyItem.range = rangeOptions;
               break;
            }
         }
      }
   }
   return survey;
};

// export const duplicateSection = (survey, sectionId) => {
//    survey.forEach((item) => {
//       if (item.itemLabel === "Section") {
//          if (item.id !== sectionId) {
//             duplicateSection(item.innerItems, sectionId);
//          } else {
//             const index = survey.findIndex((item) => item.id === sectionId);
//             if (index > -1) {
//                for (let i = index + 1; i < survey.length; i++) {
//                   survey[i].surveyItem.order -= 1;
//                }
//                const id = uuidv4();
//                // const questionItems = {
//                //    ...item.innerItems[index].surveyItem,
//                //    order: item.innerItems[index].surveyItem.order + 1,
//                //    id: id
//                // };

//                survey.splice(index, 0, survey[index]);
//                console.log(survey);
//             }
//          }
//       }
//    });
// };

export const getKeysByValues = (object, values) => {
   return values.map((value) => Object.keys(object).find((key) => object[key] === value));
};
